<template>
  <div class="editor-wrapper">
    <!-- Toolbar -->
    <div class="toolbar">
      <div style="position: relative;">
        <button v-if="isEditorReady" @click="showSymbols = true">Symbols</button>
        <!-- Symbols Popup -->
        <div v-if="showSymbols" class="symbols-popup">
          <div class="symbols-container">
            
            <div class="symbols-header">
              <span>Select Symbol</span>
              <button @click="showSymbols = false">
                <svg fill="#000000" height="10px" width="10px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 460.775 460.775" xml:space="preserve"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M285.08,230.397L456.218,59.27c6.076-6.077,6.076-15.911,0-21.986L423.511,4.565c-2.913-2.911-6.866-4.55-10.992-4.55 c-4.127,0-8.08,1.639-10.993,4.55l-171.138,171.14L59.25,4.565c-2.913-2.911-6.866-4.55-10.993-4.55 c-4.126,0-8.08,1.639-10.992,4.55L4.558,37.284c-6.077,6.075-6.077,15.909,0,21.986l171.138,171.128L4.575,401.505 c-6.074,6.077-6.074,15.911,0,21.986l32.709,32.719c2.911,2.911,6.865,4.55,10.992,4.55c4.127,0,8.08-1.639,10.994-4.55 l171.117-171.12l171.118,171.12c2.913,2.911,6.866,4.55,10.993,4.55c4.128,0,8.081-1.639,10.992-4.55l32.709-32.719 c6.074-6.075,6.074-15.909,0-21.986L285.08,230.397z"></path> </g></svg>
              </button>
            </div>

            <div class="symbols-grid">
              <span
                v-for="symbol in mathSymbols"
                :key="symbol"
                class="symbol-item"
                @click="insertSymbol(symbol)"
              >
                {{ symbol }}
              </span>
            </div>

          </div>
        </div>
      </div>
      <button v-if="isEditorReady" @click="editor.chain().focus().toggleBold().run()" :class="{ active: editor.isActive('bold') }">Bold</button>
      <button v-if="isEditorReady" @click="editor.chain().focus().toggleUnderline().run()" :class="{ active: editor.isActive('underline') }">Underline</button>
      <button v-if="isEditorReady" @click="setLink" :class="{ active: editor.isActive('link') }">Link</button>
      <button v-if="isEditorReady" @click="editor && editor.chain().focus().toggleItalic().run()" :class="{ active: editor?.isActive?.('italic') }">Italic </button>

        <button v-if="isEditorReady"
        @click="editor && editor.chain().focus().toggleSuperscript().run()"
        :class="{ active: editor?.isActive?.('superscript') }"
        >
        Sup
        </button>

        <button v-if="isEditorReady"
        @click="editor && editor.chain().focus().toggleSubscript().run()"
        :class="{ active: editor?.isActive?.('subscript') }"
        >
        Sub</button>
      <label class="upload-label"  v-if="isEditorReady">
        Upload Image
        <input type="file" accept="image/*" hidden @change="uploadImage" />
      </label>
      <button v-if="isEditorReady" @click="showFractionPopup = true">Fraction</button>
    </div>

    <div v-if="showFractionPopup" class="symbols-popup">
      <div class="symbols-container">
        <div class="symbols-header">
          <span>Insert Fraction</span>
          <button @click="showFractionPopup = false">X</button>
        </div>
        <div class="fraction-form">
          <input v-model="numerator" placeholder="Numerator" />
          <input v-model="denominator" placeholder="Denominator" />
          <button @click="insertFraction">Insert</button>
        </div>
      </div>
    </div>


    <!-- Editor content -->
    <EditorContent :editor="editor" class="editor-content" />
  </div>
</template>


<script>
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { computed,ref } from 'vue'
import Italic from '@tiptap/extension-italic'
import Superscript from '@tiptap/extension-superscript'
import Subscript from '@tiptap/extension-subscript'
import { urls } from "../assets/js/urls.js";

export default {
  name: 'WysiwygEditor',

  props: {
    modelValue: {
      type: String,
      default: ''
    }
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const showFractionPopup = ref(false)
    const numerator = ref('')
    const denominator = ref('')
    const showSymbols = ref(false)
    const CustomImage = Image.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          style: {
            default: 'max-width: 100%; height: auto;',
          },
        }
      },
    })


    const editor = useEditor({
      content: props.modelValue,
      extensions: [
        StarterKit,
        Underline,
        Link.configure({ openOnClick: false }),
        CustomImage,
        Italic,
        Superscript,
        Subscript
      ],
      onUpdate({ editor }) {
        emit('update:modelValue', editor.getHTML())
      }
    })

    const mathSymbols = ref([
      '+','-','#','?','$',
      '÷','×','±','≈','≠','≤','≥','∞','√','∛','∜','π','∑','∏','{','}','(',')','ξ','Ω','𝑈','∀','≡','∝',
      '∫','∮','∆','∇','∂','µ','Ω','α','β','γ','δ','θ','λ','σ','φ','ψ','e',
      '°','‰','⊂','⊃','⊆','⊇','∈','∉','∪','∩','∧','∨','⊕','⊗',
      '⇒','⇔','→','←','↔','↑','↓'
    ])

    const insertSymbol = (symbol) => {
      editor.value.chain().focus().insertContent(symbol).run()
      showSymbols.value = false
    }

    const uploadImage = async (event) => {
      const file = event.target.files[0]
      if (!file) return

      const formData = new FormData()
      formData.append("image", file)

      try {
        // 1. Upload to your API
        const response = await fetch(urls.upload, {
          method: "POST",
          body: formData,
        })

        const result = await response.json()

        if (result?.url) {
          editor.value
            .chain()
            .focus()
            .setImage({ src: result.url, style: "max-width: 100%;" })
            .run()
        } else {
          alert("Upload failed")
        }
      } catch (error) {
        console.error(error)
        alert("Error uploading image")
      }
    }

    const isEditorReady = computed(() => !!editor.value)

    const insertFraction = () => {
      if (!numerator.value || !denominator.value) return
      const fraction = `(${numerator.value})/(${denominator.value})`
      editor.value.chain().focus().insertContent(fraction).run()
      // reset
      numerator.value = ''
      denominator.value = ''
      showFractionPopup.value = false
    }

    return { editor, isEditorReady, uploadImage,showSymbols, mathSymbols,insertSymbol,
      showFractionPopup, numerator, denominator, insertFraction}
  },

  beforeUnmount() {
    this.editor?.destroy()
  },

  components: {
    EditorContent
  }
}
</script>


<style scoped>
.editor-wrapper {
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 6px;
  background-color: #fff;
}

.toolbar {
  margin-bottom: 10px; display: flex; flex-wrap: wrap;
}

.toolbar button,
.upload-label {
  margin-right: 5px;
  padding: 5px 10px;
  background: #f2f2f2;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.toolbar .active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.upload-label {
  display: inline-block;
  font-size: 14px;
  cursor: pointer;
}
.symbols-popup{position:absolute; width: 200px; left: 0; z-index: 9999999999999999999;}
.symbols-container{background: white; box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;}
.symbols-header {display:flex; justify-content: space-between; align-items: center; padding: 5px;}
.symbols-header button{background: none; border: 0; margin: 0;}
.symbols-header span{font-size: 12px;}
.symbols-grid{display: flex; flex-wrap: wrap;}
.symbols-grid span{display: flex; cursor: pointer; font-size: 18px; align-items: center; 
  justify-content: center; width: 25px; height: 25px;}
.symbols-grid span:hover{background:#ccc}

editor-content > :deep(div) img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 10px 0;
}

.editor-content > :deep(div){
  min-height: 200px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 15px;
  line-height: 1.5;
  background-color: #fafafa;
}

/* Tiptap-specific styles */
:deep(.ProseMirror) {
  outline: none;
}
</style>